import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { LayoutDashboard, ShoppingBag, UtensilsCrossed, ChefHat, Newspaper, Megaphone, FileText, TrendingUp, Users, Wallet, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { CrudManager } from "@/components/admin/CrudManager";
import { SettingsForm } from "@/components/admin/SettingsForm";
import { SCHEMAS } from "@/lib/adminSchemas";
import { NAV_GROUPS } from "@/lib/adminNav";
import { useAuth } from "@/context/AuthContext";
import AdminUsers from "@/pages/admin/AdminUsers";
import MyAccount from "@/pages/admin/MyAccount";
import AuditLogs from "@/pages/admin/AuditLogs";
import DataBackup from "@/pages/admin/DataBackup";
import Translations from "@/pages/admin/Translations";
import { adminOverview, adminOrders, adminAnnouncements, adminPages, savePage, fetchChefs, fetchDishes, fetchBlog, crud, adminCategories, resolveImg } from "@/lib/apiData";
import { euro } from "@/context/CartContext";

const ORIGINAL_LOGO = "/jollanda-logo-light.webp";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-neutral-200 p-6" data-testid={`overview-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <Icon className="w-6 h-6 text-[#d00000] mb-4" />
    <p className="font-display text-4xl font-black text-neutral-900">{value}</p>
    <p className="text-sm text-neutral-500 mt-1">{label}</p>
  </div>
);

function Overview() {
  const { data } = useQuery({ queryKey: ["admin-overview"], queryFn: adminOverview });
  if (!data) return <p className="text-neutral-400">Loading…</p>;
  return (
    <div data-testid="admin-overview">
      <h2 className="font-display text-3xl font-black text-neutral-900 mb-6">Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ShoppingBag} label="Total orders" value={data.total_orders} />
        <StatCard icon={Wallet} label="Revenue" value={euro(data.revenue)} />
        <StatCard icon={Users} label="Active customers" value={data.active_customers} />
        <StatCard icon={Users} label="Total users" value={data.total_users} />
        <StatCard icon={ChefHat} label="Chefs" value={data.total_chefs} />
        <StatCard icon={UtensilsCrossed} label="Dishes" value={data.total_dishes} />
        <StatCard icon={Newspaper} label="Blog posts" value={data.total_blog} />
        <StatCard icon={TrendingUp} label="Avg order" value={euro(data.avg_order_value)} />
      </div>

      <div className="bg-white border border-neutral-200 p-6 mt-6">
        <p className="text-xs uppercase tracking-widest text-neutral-500 mb-4">Revenue · last 7 days</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" stroke="#999" fontSize={12} />
              <YAxis stroke="#999" fontSize={12} />
              <Tooltip formatter={(v) => euro(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#d00000" strokeWidth={2} dot={{ fill: "#d00000" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 mt-6">
        <p className="text-xs uppercase tracking-widest text-neutral-500 p-6 pb-0">Recent orders</p>
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.recent_orders.map((o) => (
              <TableRow key={o.id}>
                <TableCell className="font-medium text-neutral-900">{o.order_no}</TableCell>
                <TableCell className="text-neutral-600">{o.customer_name}</TableCell>
                <TableCell className="text-neutral-900">{euro(o.total)}</TableCell>
                <TableCell><span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 capitalize">{o.status}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Orders() {
  const { data: orders = [] } = useQuery({ queryKey: ["admin-orders"], queryFn: adminOrders });
  return (
    <div data-testid="admin-orders-panel">
      <h2 className="font-display text-3xl font-black text-neutral-900 mb-6">Orders</h2>
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.order_no}`}>
                <TableCell className="font-medium text-neutral-900">{o.order_no}</TableCell>
                <TableCell className="text-neutral-600">{o.customer_name}</TableCell>
                <TableCell className="text-neutral-600">{(o.items || []).reduce((n, i) => n + (i.qty || 1), 0)}</TableCell>
                <TableCell className="text-neutral-900">{euro(o.total)}</TableCell>
                <TableCell><span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 capitalize">{o.status}</span></TableCell>
                <TableCell className="text-neutral-400 text-sm">{(o.created_at || "").slice(0, 10)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Pages() {
  const qc = useQueryClient();
  const { data: pages = [] } = useQuery({ queryKey: ["admin-pages"], queryFn: adminPages });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", body: "" });

  const open = (p) => { setEditing(p); setForm({ title: p.title, body: p.body }); };
  const save = async (e) => {
    e.preventDefault();
    await savePage(editing.slug, { title: form.title, body: form.body });
    qc.invalidateQueries({ queryKey: ["admin-pages"] });
    toast.success("Page updated");
    setEditing(null);
  };

  return (
    <div data-testid="admin-pages-panel">
      <h2 className="font-display text-3xl font-black text-neutral-900 mb-6">Web Pages</h2>
      <p className="text-neutral-500 mb-6 text-sm">Edit the content of your informational pages. Changes go live instantly.</p>
      <div className="border border-neutral-200 bg-white divide-y divide-neutral-100">
        {pages.map((p) => (
          <div key={p.slug} className="flex items-center justify-between p-4" data-testid={`page-row-${p.slug}`}>
            <div>
              <p className="font-medium text-neutral-900">{p.title}</p>
              <p className="text-xs text-neutral-400">/{p.slug}</p>
            </div>
            <Button onClick={() => open(p)} data-testid={`edit-page-${p.slug}`} variant="ghost" className="border border-neutral-300 rounded-none">Edit</Button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="page-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit page</DialogTitle>
            <DialogDescription className="sr-only">Edit the page title and body.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="page-title" className="mt-1 h-11 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Body</Label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={8} data-testid="page-body" className="mt-1 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <DialogFooter><Button type="submit" data-testid="page-save-btn" className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">Save changes</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !i.perm || perms.includes(i.perm)) })).filter((g) => g.items.length);
  const [tab, setTab] = useState(() => groups[0]?.items[0]?.id || "account");
  const { data: chefs = [] } = useQuery({ queryKey: ["chefs"], queryFn: () => fetchChefs() });
  const { data: dishes = [] } = useQuery({ queryKey: ["dishes"], queryFn: () => fetchDishes() });
  const chefOptions = chefs.map((c) => ({ value: c.id, label: c.name }));
  const dishOptions = dishes.map((d) => ({ value: d.id, label: d.name }));

  return (
    <div className="min-h-screen bg-[#fbf9f7] flex flex-col md:flex-row" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="md:w-64 bg-neutral-900 text-white md:min-h-screen flex-shrink-0 md:sticky md:top-0 md:h-screen md:overflow-y-auto">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="block transition-opacity hover:opacity-80" data-testid="admin-logo-link">
            <img src={ORIGINAL_LOGO} alt="Jollanda" className="h-12 w-auto object-contain" />
          </Link>
          <p className="text-xs text-neutral-400 mt-3 tracking-widest uppercase">Admin</p>
        </div>
        <nav className="p-3 flex md:flex-col gap-1 overflow-x-auto">
          {groups.map((g) => (
            <div key={g.title} className="flex md:flex-col gap-1 md:mb-3">
              <p className="hidden md:block px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.2em] text-neutral-500">{g.title}</p>
              {g.items.map((n) => (
                <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-tab-${n.id}`}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm whitespace-nowrap transition-colors ${tab === n.id ? "bg-[#d00000] text-white" : "text-neutral-300 hover:bg-white/5"}`}>
                  <n.icon className="w-4 h-4" /> {n.label}
                </button>
              ))}
            </div>
          ))}
          <Link to="/" className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-400 hover:bg-white/5" data-testid="admin-back-to-site"><HomeIcon className="w-4 h-4" /> Back to site</Link>
        </nav>
      </aside>

      {/* Content */}
      <div className="flex-1 p-6 sm:p-10 overflow-x-hidden">
        {SCHEMAS[tab] && <SettingsForm key={tab} sectionKey={tab} {...SCHEMAS[tab]} options={{ chefs: chefOptions, dishes: dishOptions }} />}
        {tab === "users" && <AdminUsers />}
        {tab === "account" && <MyAccount />}
        {tab === "audit" && <AuditLogs />}
        {tab === "data" && <DataBackup />}
        {tab === "translations" && <Translations />}
        {tab === "categories" && (
          <CrudManager
            title="Food category" queryKey="admin-categories" fetchFn={adminCategories} api={crud("categories")} extraInvalidateKeys={["categories"]}
            columns={[
              { key: "image", label: "", render: (r) => <img src={resolveImg(r.image)} alt="" className="w-12 h-12 object-cover" /> },
              { key: "name", label: "Name" },
              { key: "tag", label: "Tagline" },
              { key: "count", label: "Dishes" },
              { key: "order", label: "Order" },
              { key: "active", label: "Active", render: (r) => (r.active ? "Yes" : "No") },
              { key: "featured", label: "On homepage", render: (r) => (r.featured ? "Yes" : "No") },
            ]}
            fields={[
              { name: "name", label: "Name (must match the dish cuisine)", type: "text" },
              { name: "tag", label: "Tagline", type: "text" },
              { name: "order", label: "Display order", type: "number" },
              { name: "image", label: "Image", type: "image" },
              { name: "active", label: "Active", type: "checkbox" },
              { name: "featured", label: "Show on homepage", type: "checkbox" },
            ]}
          />
        )}
        {tab === "overview" && <Overview />}
        {tab === "orders" && <Orders />}
        {tab === "pages" && <Pages />}
        {tab === "dishes" && (
          <CrudManager
            title="Dish" queryKey="dishes" fetchFn={() => fetchDishes()} api={crud("dishes")}
            columns={[
              { key: "img", label: "", render: (r) => <img src={r.img} alt="" className="w-12 h-12 object-cover" /> },
              { key: "name", label: "Name" },
              { key: "cuisine", label: "Cuisine" },
              { key: "chef", label: "Chef" },
              { key: "price", label: "Price", render: (r) => euro(r.price || 0) },
            ]}
            fields={[
              { name: "name", label: "Name", type: "text" },
              { name: "cuisine", label: "Cuisine", type: "text" },
              { name: "chef", label: "Chef", type: "select", options: chefOptions },
              { name: "price", label: "Price (€)", type: "number" },
              { name: "spicy", label: "Spicy (0-3)", type: "number" },
              { name: "rating", label: "Rating", type: "number" },
              { name: "img", label: "Dish photo", type: "image" },
              { name: "tags", label: "Tags (comma separated)", type: "tags" },
              { name: "desc", label: "Description", type: "textarea" },
            ]}
          />
        )}
        {tab === "chefs" && (
          <CrudManager
            title="Chef" queryKey="chefs" fetchFn={() => fetchChefs()} api={crud("chefs")}
            columns={[
              { key: "img", label: "", render: (r) => <img src={r.img} alt="" className="w-12 h-12 object-cover" /> },
              { key: "name", label: "Name" },
              { key: "type", label: "Type" },
              { key: "cuisine", label: "Cuisine" },
              { key: "rating", label: "Rating" },
            ]}
            fields={[
              { name: "name", label: "Name", type: "text" },
              { name: "type", label: "Type", type: "select", options: [{ value: "chef", label: "Chef" }, { value: "restaurant", label: "Restaurant" }] },
              { name: "kitchen", label: "Kitchen name", type: "text" },
              { name: "cuisine", label: "Cuisine", type: "text" },
              { name: "city", label: "City", type: "text" },
              { name: "img", label: "Portrait photo", type: "image" },
              { name: "cover", label: "Cover photo", type: "image" },
              { name: "signature", label: "Signature dish", type: "text" },
              { name: "years", label: "Years experience", type: "number" },
              { name: "rating", label: "Rating", type: "number" },
              { name: "reviews", label: "Reviews", type: "number" },
              { name: "verified", label: "Verified", type: "checkbox" },
              { name: "bio", label: "Bio", type: "textarea" },
            ]}
          />
        )}
        {tab === "blog" && (
          <CrudManager
            title="Blog post" queryKey="blog" fetchFn={fetchBlog} api={crud("blog")}
            columns={[
              { key: "cover", label: "", render: (r) => <img src={r.cover} alt="" className="w-12 h-12 object-cover" /> },
              { key: "title", label: "Title" },
              { key: "category", label: "Category" },
              { key: "author", label: "Author" },
            ]}
            fields={[
              { name: "title", label: "Title", type: "text" },
              { name: "category", label: "Category", type: "text" },
              { name: "read", label: "Read time (e.g. 6 min)", type: "text" },
              { name: "author", label: "Author", type: "text" },
              { name: "cover", label: "Cover photo", type: "image" },
              { name: "excerpt", label: "Excerpt", type: "textarea" },
              { name: "body", label: "Body", type: "textarea" },
            ]}
          />
        )}
        {tab === "announcements" && (
          <CrudManager
            title="Announcement" queryKey="admin-announcements" fetchFn={adminAnnouncements} api={crud("announcements")} extraInvalidateKeys={["announcements"]}
            columns={[
              { key: "message", label: "Message" },
              { key: "active", label: "Active", render: (r) => (r.active ? "Yes" : "No") },
            ]}
            fields={[
              { name: "message", label: "Message", type: "text" },
              { name: "active", label: "Active", type: "checkbox" },
            ]}
          />
        )}
      </div>
    </div>
  );
}
